import * as React from 'react';
import {
  // eslint-disable-next-line no-restricted-imports
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
} from '@material-ui/core';
import {
  OmakaseTextColorsWithInherit,
  OmakaseTypographyVariant,
} from 'theme/typography/omakaseTypography';
import { Theme, css } from '@emotion/react';

interface OmakaseTypographyProps extends Omit<MuiTypographyProps, 'color' | 'variant'> {
  /**
   * Color of the text, based on theme text colors. Defaults to fgDefault.
   */
  color?: OmakaseTextColorsWithInherit;
  /**
   * Controls font-family, font-size, font-weight, line-height, letter-spacing, text-transform.
   */
  variant?: OmakaseTypographyVariant;

  /**
   * The HTML element to be rendered by the Typography component.
   */
  component?: React.ElementType;
  /**
   * The bottom margin, calculated using the theme.spacing() function in increments of 8
   * (e.g., mb={4} results in margin-bottom: '32px' being applied to the element)
   */
  mb?: number;
}

export type TypographyProps = OmakaseTypographyProps;

/**
 * Use `Typography` for text
 *
 * ```js
 * import { Typography, TypographyProps } from 'omakase';
 * ```
 */
const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      color = 'fgDefault',
      variant = 'body02',
      className,
      component,
      paragraph,
      children,
      mb,
      style,
      ...rest
    },
    ref
  ) => {
    // Compute CSS interpolation for Omakase variant
    const computeOmakaseVariantStyles = (
      { typography }: Theme,
      variant: OmakaseTypographyVariant
    ) => {
      return css`
        ${typography.variant(variant)}
      `;
    };

    // Compute CSS interpolation for Omakase color
    const computeOmakaseColorStyles = ({ colors }: Theme, color: OmakaseTextColorsWithInherit) => {
      return css`
        color: ${color === 'inherit' ? color : colors[color]};
      `;
    };

    return (
      <MuiTypography
        {...rest}
        ref={ref}
        className={className}
        variant="inherit"
        css={(theme: Theme) => {
          return [
            computeOmakaseVariantStyles(theme, variant),
            computeOmakaseColorStyles(theme, color),
            mb !== undefined && `margin-bottom: ${theme.spacing(mb)}px;`,
          ];
        }}
        component={component ?? 'p'}
      >
        {children}
      </MuiTypography>
    );
  }
);

export default Typography;
